// Money pulse — the exec money row (Ship 3, Aug 26 2026).
//
// Reads `financials_pulse_stats()`, the SAME SECURITY DEFINER RPC that powers
// Shannon/Chad/Greg's Monday 7am pulse text. One call carries the whole money
// picture (billed / collected / signings for the week, AR, SRS owed, crew owed,
// stale backlog), so there is no second money surface on this board and nothing
// can drift from the Monday text.
//
// WINDOW: the RPC reports the LAST COMPLETE week (`date_trunc('week', today)
// - 7`), not week-to-date. That is deliberate — a partial week reads low and
// would look like a collapse every Monday — but it means the header must SAY
// which week it is. It does.
//
// ACCESS: `financials_gate()` admits admin/sales_director/finance and hard-
// blocks jcohan@ even though his role resolves to admin. The registry mirrors
// that carve-out via `excludeEmails`, so this widget is never mounted for
// someone whose RPC would 42501 — but the error branch below is real, not
// decorative, because the gate is the server's call, not ours.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../../lib/supabase";
import { logCardClick } from "../../../lib/uiEvents";

interface Pulse {
  week_from: string;
  week_to: string;
  billed: number;
  builds: number;
  prev_billed: number;
  collected: number;
  signings: number;
  ar_total: number;
  srs_unpaid: number;
  srs_overdue: number;
  crew_owed: number;
  backlog_stale30: number;
}

const n = (v: unknown) => Number(v ?? 0);
const usd0 = (v: number) => `$${Math.round(v).toLocaleString()}`;
const usdK = (v: number) =>
  Math.abs(v) >= 10_000 ? `$${Math.round(v / 1000).toLocaleString()}K` : usd0(v);

export default function PnlWidget() {
  const [p, setP] = useState<Pulse | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.rpc("financials_pulse_stats");
      if (cancelled) return;
      if (error || !data) {
        setFailed(true);
        return;
      }
      setP(data as unknown as Pulse);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const delta =
    p && n(p.prev_billed) > 0
      ? Math.round(((n(p.billed) - n(p.prev_billed)) / n(p.prev_billed)) * 100)
      : null;

  const tile = (label: string, value: string, sub?: string, tone?: "rose" | "amber") => (
    <div className="min-w-0">
      <div
        className={`text-lg font-extrabold leading-tight truncate ${
          tone === "rose" ? "text-rose-700" : tone === "amber" ? "text-amber-700" : "text-bardin-ink"
        }`}
      >
        {value}
      </div>
      <div className="text-[10px] uppercase tracking-wide font-bold text-slate-400 truncate">
        {label}
      </div>
      {sub && <div className="text-[10px] text-slate-500 font-semibold truncate">{sub}</div>}
    </div>
  );

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 mb-2">
        <span>💹</span>
        <span className="font-bold text-sm text-bardin-ink">Money pulse</span>
        <span className="ml-auto text-xs font-bold text-slate-500">
          {failed ? "—" : p ? `week of ${p.week_from}–${p.week_to}` : "…"}
        </span>
      </div>

      {failed ? (
        <div className="text-xs text-slate-400 py-2">Couldn't load the money pulse — try a refresh.</div>
      ) : !p ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-10 rounded-lg bg-slate-100 animate-pulse" />
          ))}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-3">
            {tile(
              "Billed",
              usdK(n(p.billed)),
              `${n(p.builds)} build${n(p.builds) === 1 ? "" : "s"}${
                delta == null ? "" : ` · ${delta >= 0 ? "+" : ""}${delta}% vs prior`
              }`,
            )}
            {tile("Collected", usdK(n(p.collected)), `${n(p.signings)} signings`)}
            {tile("AR outstanding", usdK(n(p.ar_total)), "collecting + delinquent")}
            {tile(
              "Owed out",
              usdK(n(p.srs_unpaid) + n(p.crew_owed)),
              `SRS ${usdK(n(p.srs_unpaid))} · crew ${usdK(n(p.crew_owed))}`,
              n(p.srs_overdue) > 0 ? "amber" : undefined,
            )}
          </div>

          {(n(p.srs_overdue) > 0 || n(p.backlog_stale30) > 0) && (
            <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2.5 pt-2 border-t border-slate-100 text-[10.5px] font-semibold">
              {n(p.srs_overdue) > 0 && (
                <span className="text-rose-700">
                  ⚠ {usd0(n(p.srs_overdue))} of SRS invoices past due
                </span>
              )}
              {n(p.backlog_stale30) > 0 && (
                <span className="text-amber-700">
                  ⚠ {n(p.backlog_stale30)} signed jobs unscheduled 30d+
                </span>
              )}
            </div>
          )}
        </>
      )}

      <div className="flex mt-2 pt-2 border-t border-slate-100">
        <Link
          to="/financials"
          onClick={() => logCardClick("widget_pnl_open")}
          className="ml-auto text-[11px] font-bold text-bardin-orange"
        >
          Financials ›
        </Link>
      </div>
    </div>
  );
}
